/**
 * Token display data (CR 187; research 03 §6.4; owner decision Q46: styled placeholders where there is
 * no art). The engine's `TOKEN_KINDS` say what each token *is*; this table says how to show it: name,
 * text, and the catalogue card/printing for the three tokens Runebound has art for.
 */
import type { TokenId } from '@play/engine';

/** How a token kind is shown. */
export interface TokenDisplay {
  id: TokenId;
  name: string;
  /** Rules text (CR 187 or the card's reminder text). */
  text: string;
  /** Catalogue card with this token's art, if any (Recruit, Sprite, Gold). */
  catalogCardId: string | null;
  /** Default printing for the art, or `null`: render the styled placeholder. */
  printingId: string | null;
}

const rows: TokenDisplay[] = [
  {
    id: 'token:recruit',
    name: 'Recruit',
    text: '1 Might Recruit unit.',
    catalogCardId: 'recruit-271-buff',
    printingId: 'ogn-271-298',
  },
  {
    id: 'token:sprite',
    name: 'Sprite',
    text: '3 Might Fae unit. [Temporary]',
    catalogCardId: 'sprite-274-buff',
    printingId: 'ogn-274-298',
  },
  {
    id: 'token:sand-soldier',
    name: 'Sand Soldier',
    text: '2 Might Shurima unit.',
    catalogCardId: null,
    printingId: null,
  },
  { id: 'token:mech', name: 'Mech', text: '3 Might Mech unit.', catalogCardId: null, printingId: null },
  {
    id: 'token:gold',
    name: 'Gold',
    text: 'Gear. [Reaction][>] Kill this, [E]: [Add] [A].',
    catalogCardId: 'gold-buff',
    printingId: 'sfd-t03',
  },
  {
    id: 'token:reflection',
    name: 'Reflection',
    text: '0 Might unit, usually given a copy effect.',
    catalogCardId: null,
    printingId: null,
  },
  { id: 'token:bird', name: 'Bird', text: '1 Might Bird unit. [Deflect]', catalogCardId: null, printingId: null },
  {
    id: 'token:brush',
    name: 'Brush',
    text: 'Battlefield. Bird, Cat, Dog, Poro and Ivern units here have +1 [M]. When you score here, you may replace this with the battlefield it replaced.',
    catalogCardId: null,
    printingId: null,
  },
  {
    id: 'token:baron-pit',
    name: 'Baron Pit',
    text: 'Battlefield. Units can move here from anywhere.',
    catalogCardId: null,
    printingId: null,
  },
  { id: 'token:tentacle', name: 'Tentacle', text: '1 Might Bilgewater unit.', catalogCardId: null, printingId: null },
  {
    id: 'token:shadow-clone',
    name: 'Shadow Clone',
    text: '0 Might unit. When I attack, you may banish a unit from your trash. If you do, give me [Assault 4] this turn.',
    catalogCardId: null,
    printingId: null,
  },
  {
    id: 'token:bomb',
    name: 'Bomb',
    text: 'Gear. [Deploy]. [Deathknell] Deal 2 to an enemy unit here and kill all other Bombs you control here.',
    catalogCardId: null,
    printingId: null,
  },
];

/** Display data for every token kind, by token id. */
export const TOKEN_DISPLAY: Readonly<Record<TokenId, TokenDisplay>> = Object.fromEntries(rows.map((r) => [r.id, r]));

/** Display data for a token id, or `undefined` for an unknown one. */
export function tokenDisplay(id: string): TokenDisplay | undefined {
  return (TOKEN_DISPLAY as Readonly<Record<string, TokenDisplay | undefined>>)[id];
}
